import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, Info, Layers, Loader2, Sparkles } from 'lucide-react';
import { documentStoreService } from '@/services/document-store.service';
import type {
  CourseDeliveryType,
  CourseStructureAnalysis,
} from '@/types/document-store.types';

interface KnowledgeStoreAnalysisPanelProps {
  /** Empty when no store is selected — the panel renders nothing. */
  knowledgeStoreId: string;
  /** Decides whether a scene range, a video duration, or neither is suggested. */
  deliveryType: CourseDeliveryType;
  /**
   * The course builder's own ceilings. Passed rather than assumed so the panel and the
   * sliders can never disagree about what the admin is allowed to enter.
   */
  limits: {
    maxModules: number;
    maxContentItemsPerModule: number;
    minScenesPerContent: number;
    maxScenesPerContent: number;
    minDurationSeconds: number;
    maxDurationSeconds: number;
  };
  /** Applies the suggested range to the builder's controls. Hidden when not supplied. */
  onApply?: (suggestion: CourseStructureAnalysis['suggested']) => void;
  /**
   * Hands the loaded suggestion to the page, so it can tell whether the controls match it.
   * Called with null when there is nothing to compare against.
   */
  onAnalysis?: (suggestion: CourseStructureAnalysis['suggested'] | null) => void;
  /** True while the controls hold the suggested range, so the button can confirm it. */
  isApplied?: boolean;
}

/** "3" when the ends match, "3 – 6" when they differ. */
function formatRange(min?: number, max?: number): string {
  if (min == null || max == null) return '—';
  return min === max ? String(min) : `${min} – ${max}`;
}

/** Seconds as minutes, dropping a trailing ".0" — "4 min", "4.5 min". */
function formatMinutes(seconds?: number): string {
  if (seconds == null) return '—';
  const value = seconds / 60;
  return `${Number.isInteger(value) ? value : value.toFixed(1)} min`;
}

function formatMinuteRange(min?: number, max?: number): string {
  if (min == null || max == null) return '—';
  if (min === max) return formatMinutes(max);
  const low = min / 60;
  return `${Number.isInteger(low) ? low : low.toFixed(1)} – ${formatMinutes(max)}`;
}

/**
 * Reports what a knowledge store's documents can support as a course: the range of modules and
 * content items the material justifies, the scene range for a video presentation or the video
 * length for an avatar course, and an explicit warning when the documents hold more detail
 * than a course of the permitted size can teach.
 *
 * Rendered after the course type is chosen, because the type decides which of scenes and
 * duration applies — an avatar course has no scenes, it has a length per content item.
 *
 * The analysis itself is cached server-side against the store's documents, so switching course
 * type re-derives from that one reading rather than paying for another.
 */
export default function KnowledgeStoreAnalysisPanel({
  knowledgeStoreId,
  deliveryType,
  limits,
  onApply,
  onAnalysis,
  isApplied = false,
}: KnowledgeStoreAnalysisPanelProps) {
  // html and html_with_audio differ only in narration, never in structure, so they share a
  // cache entry. The query key carries the limits too: the same store analysed against the
  // avatar caps is a different suggestion from one analysed against the HTML caps.
  const structuralType: CourseDeliveryType = deliveryType === 'html_with_audio' ? 'html' : deliveryType;

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'ks-structure-analysis',
      knowledgeStoreId,
      structuralType,
      limits.maxModules,
      limits.maxContentItemsPerModule,
      limits.minScenesPerContent,
      limits.maxScenesPerContent,
      limits.minDurationSeconds,
      limits.maxDurationSeconds,
    ],
    queryFn: () =>
      documentStoreService.getCourseStructureAnalysis(knowledgeStoreId, {
        delivery_type: structuralType,
        max_modules: limits.maxModules,
        max_content_items_per_module: limits.maxContentItemsPerModule,
        min_scenes_per_content: limits.minScenesPerContent,
        max_scenes_per_content: limits.maxScenesPerContent,
        min_duration_seconds: limits.minDurationSeconds,
        max_duration_seconds: limits.maxDurationSeconds,
      }),
    enabled: !!knowledgeStoreId,
    // The server caches against the documents themselves, so a repeat visit costs nothing.
    // This only keeps the panel from re-fetching while the admin fills in the rest of the form.
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  // Publish the suggestion upward whenever it changes — including to null while it is loading
  // or when the store could not be analysed, so a previous store's numbers are never left
  // standing as the thing the controls are compared against.
  const suggestion = data?.analysis_available ? data.suggested : null;
  useEffect(() => {
    onAnalysis?.(suggestion ?? null);
    // The callback is recreated every render by the parent; depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion]);

  if (!knowledgeStoreId) return null;

  if (isLoading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-2.5">
          <Loader2 className="h-4 w-4 animate-spin text-brand-purple-500" />
          <div>
            <p className="text-sm font-medium text-gray-900">Analysing your documents</p>
            <p className="text-xs text-gray-500">
              Reading the knowledge store to work out how much course it can support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // A failed analysis is advice that did not arrive, not an error the admin must resolve.
  if (isError || !data) {
    return (
      <div className="flex items-start gap-2.5 rounded-xl border border-gray-200 bg-gray-50 p-4">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
        <p className="text-xs text-gray-600">
          The documents could not be analysed just now. You can still set the course size yourself
          in Advanced Settings.
        </p>
      </div>
    );
  }

  if (!data.analysis_available) {
    return (
      <div className="flex items-start gap-2.5 rounded-xl border border-gray-200 bg-gray-50 p-4">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
        <p className="text-xs text-gray-600">{data.message}</p>
      </div>
    );
  }

  const s = data.suggested;

  // Every ceiling comes back with the analysis, so the panel states the real limit rather
  // than repeating a number that could drift away from the sliders.
  const applied = data.limits;

  const metrics = [
    {
      label: 'Modules',
      value: formatRange(s.min_modules, s.max_modules),
      hint: `Max ${applied.max_modules}`,
    },
    {
      label: 'Content items',
      value: formatRange(s.min_content_items_per_module, s.max_content_items_per_module),
      hint: `Max ${applied.max_content_items_per_module} per module`,
    },
    ...(data.includes_duration
      ? [
          {
            label: 'Video length',
            value: formatMinuteRange(s.min_duration_seconds, s.max_duration_seconds),
            hint: `Max ${formatMinutes(applied.max_duration_seconds)} per content item`,
          },
        ]
      : data.includes_scenes
        ? [
            {
              label: 'Scenes',
              value: formatRange(s.min_scenes_per_content, s.max_scenes_per_content),
              hint: `Max ${applied.max_scenes_per_content} per content item`,
            },
          ]
        : []),
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {/* Header */}
      <div className="flex items-start gap-3 border-b border-gray-100 bg-gradient-to-r from-brand-purple-50 to-white px-4 py-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-purple-100">
          <Sparkles className="h-4 w-4 text-brand-purple-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900">Suggested course size</p>
          <p className="truncate text-xs text-gray-500" title={data.document_store_name}>
            Based on {data.document_names.length}{' '}
            {data.document_names.length === 1 ? 'document' : 'documents'} in{' '}
            {data.document_store_name}
          </p>
        </div>
        {onApply && (
          <button
            type="button"
            disabled={isApplied}
            onClick={() => onApply(s)}
            className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
              isApplied
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-brand-purple-600 text-white hover:bg-brand-purple-700'
            }`}
          >
            {isApplied && <Check className="h-3.5 w-3.5" />}
            {isApplied ? 'Applied' : 'Use this range'}
          </button>
        )}
      </div>

      <div className="space-y-4 p-4">
        {/* The range */}
        <div className={`grid gap-2 ${metrics.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-center"
            >
              <p className="text-lg font-semibold text-gray-900">{metric.value}</p>
              <p className="text-xs font-medium text-gray-700">{metric.label}</p>
              <p className="text-[10px] text-gray-400">{metric.hint}</p>
            </div>
          ))}
        </div>

        {data.detail_may_be_omitted ? (
          /* The documents hold more than a course of this size can teach. The wording comes
             from the server so the panel states the outcome once, without the counts and
             ceilings the decision was made from. */
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="space-y-1">
              <p className="text-xs font-semibold text-amber-900">
                Your documents hold more than one course can teach
              </p>
              <p className="text-xs leading-relaxed text-amber-800">{data.omitted_detail_note}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
            <Layers className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <div className="space-y-1">
              <p className="text-xs font-semibold text-blue-900">
                This material fits in one course
              </p>
              <p className="text-xs leading-relaxed text-blue-800">{data.message}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
